import _ from 'lodash';

import * as helpIds from '#packages/helpIds';
import constants from '#packages/constants';
import gfppDataUtils from '../../utils/gfppDataUtils';
import columnsGfppUtils from '../../utils/columnsGfppUtils';
import { designData } from '#packages/util';

import type { EditorAPI } from '#packages/editorAPI';
import type { CompRef } from 'types/documentServices';
import type { GFPPActionType } from '@wix/editor-component-types';
import { handleOnDesignPanelClick } from '../../utils/stripColumnsContainerGfppUtils';
import * as gfppData from '#packages/gfppData';

const COLUMNS_BG_PANEL_NAME =
  'compPanels.panels.StripColumnsContainer.backgroundPanel';
const { ACTIONS } = constants.ROOT_COMPS.GFPP;

const hasVideoBackground = (editorAPI: EditorAPI, column: CompRef) => {
  const designData = editorAPI.components.design.get(column);
  return designData?.background?.mediaRef?.type === constants.MEDIA_TYPES.VIDEO;
};
const hasPadding = (editorAPI: EditorAPI, column: CompRef) => {
  const properties = editorAPI.components.properties.get(column) as {
    mediaBackgroundPadding?: Record<string, number>;
  };
  const mediaBackgroundPaddingProp = properties?.mediaBackgroundPadding;
  return (
    mediaBackgroundPaddingProp &&
    Object.values(mediaBackgroundPaddingProp)[0] > 0
  );
};

const hasChildrenWithBackgroundVideoAndPadding = (
  editorAPI: EditorAPI,
  columnsContainer: CompRef,
) =>
  _(columnsContainer)
    .thru(editorAPI.components.getChildren)
    .filter((column) => hasVideoBackground(editorAPI, column))
    .filter((column) => hasPadding(editorAPI, column))
    .value().length > 0;

const shouldShowMobileBackgroundSettingsAction = (
  editorAPI: EditorAPI,
  columnsContainer: CompRef,
) =>
  columnsGfppUtils.hasMargins(editorAPI, columnsContainer) ||
  columnsGfppUtils.hasChildrenWithOpacity(editorAPI, columnsContainer) ||
  hasChildrenWithBackgroundVideoAndPadding(editorAPI, columnsContainer);

function getChangeBackgroundActionIfApplicable(
  editorAPI: EditorAPI,
  columnsContainerPtr: CompRef,
) {
  const isSingleColumn =
    editorAPI.columns.isStripWithSingleColumn(columnsContainerPtr);
  const stripColumns = editorAPI.components.getChildren(columnsContainerPtr);
  const componentForBgPanel =
    isSingleColumn &&
    !designData.hasDividersDesign(editorAPI, columnsContainerPtr)
      ? stripColumns
      : columnsContainerPtr;
  const isBackgroundVisible = columnsGfppUtils.shouldShowChangeBackgroundAction(
    editorAPI,
    columnsContainerPtr,
    isSingleColumn,
  );

  const { label, icon, tooltip, backgroundMediaType } =
    gfppDataUtils.getDataGfppWithBackgroundIcon(
      editorAPI,
      Array.isArray(componentForBgPanel)
        ? componentForBgPanel[0]
        : componentForBgPanel,
      {
        tooltip: !isBackgroundVisible
          ? 'gfpp_tooltip_mainaction_stripContainer_bg_scroll_effects_unsupported'
          : '',
        isDisabled: !isBackgroundVisible,
      },
    );

  return {
    label,
    isSelected: gfppDataUtils.getPanelStateFn(COLUMNS_BG_PANEL_NAME),
    onClick: gfppDataUtils.getToggleCompPanelFn(COLUMNS_BG_PANEL_NAME, {
      selectedComponent: componentForBgPanel,
      focusedCompType: editorAPI.components.getType(componentForBgPanel),
      prependNoFillPreset: !isSingleColumn,
      biParams: { bg_media_type: backgroundMediaType },
    }),
    automationId: 'gfpp-button-background',
    isDisabled: !isBackgroundVisible,
    tooltip,
    icon,
  };
}

function getMainActions(editorAPI: EditorAPI, compRef: CompRef) {
  return [
    columnsGfppUtils.getManageColumnsActionIfApplicable(editorAPI, compRef),
    editorAPI.isDesktopEditor() &&
      getChangeBackgroundActionIfApplicable(editorAPI, compRef),
  ].filter(Boolean);
}

export default {
  untranslatable: true,
  mainActions: getMainActions,
  enabledActions: (editorAPI: EditorAPI) => {
    const animationActionMapEntry =
      editorAPI.components.effects?.usesNewBgScrub()
        ? ACTIONS.ANIMATION
        : ACTIONS.EFFECTS;

    const actions: GFPPActionType[] = [
      animationActionMapEntry,
      ACTIONS.LAYOUT,
      ACTIONS.STRETCH_FOR_COLUMNS,
      ACTIONS.HELP,
      ACTIONS.DIVIDERS,
      ACTIONS.DESIGN,
    ];
    return actions;
  },
  mobileMainActions: getMainActions,
  mobileEnabledActions(editorAPI: EditorAPI, compRef: CompRef) {
    const isSingleColumn = editorAPI.columns.isStripWithSingleColumn(compRef);
    const shouldShowMobileBackgroundSettings =
      isSingleColumn ||
      shouldShowMobileBackgroundSettingsAction(editorAPI, compRef);
    const shouldDisplayDividers = designData.hasDividersDesign(
      editorAPI,
      compRef,
    );
    const animationActionMapEntry =
      editorAPI.components.effects?.usesNewBgScrub()
        ? ACTIONS.ANIMATION
        : ACTIONS.EFFECTS;

    const actions = [
      isSingleColumn && animationActionMapEntry,
      !isSingleColumn && ACTIONS.LAYOUT,
      ACTIONS.HIDE,
      ACTIONS.HELP,
      shouldShowMobileBackgroundSettings && ACTIONS.MOBILE_BACKGROUND_SETTINGS,
    ] as GFPPActionType[];

    return actions.concat(shouldDisplayDividers ? [ACTIONS.DIVIDERS] : []);
  },
  presetActions(editorAPI: EditorAPI, compRef: CompRef) {
    const isSingleColumn = editorAPI.columns.isStripWithSingleColumn(compRef);
    const helpId = isSingleColumn
      ? helpIds.GFPP.STRIP_COLUMNS_CONTAINER_SINGLE
      : helpIds.GFPP.STRIP_COLUMNS_CONTAINER;
    const mobileHelpId = isSingleColumn
      ? helpIds.GFPP.STRIP_COLUMNS_CONTAINER_MOBILE_SINGLE
      : helpIds.GFPP.STRIP_COLUMNS_CONTAINER_MOBILE;

    return {
      help: {
        helpId,
        mobileHelpId,
      },
      design: {
        onClick: handleOnDesignPanelClick,
      },
      animation: {
        isApplied: (editorAPI: EditorAPI, compRef: CompRef) => {
          const isStrip = editorAPI.columns.isStrip(compRef);
          const compDesign = editorAPI.components.design.get(compRef);
          const hasShapeDividers = !!(
            compDesign?.dividerTop || compDesign?.dividerBottom
          );
          const currCompRef =
            isStrip && !hasShapeDividers
              ? editorAPI.columns.getColumnIfStripIsSingleColumn(compRef)
              : compRef;
          return gfppData.utils.isAnimationGfppApplied(editorAPI, currCompRef);
        },
      },
    };
  },
};
